import { asPatientDocumentId, type PatientDocumentId, type ResourcePointer } from "@repo/domain";
import { z } from "zod";
import { newEntityIdSchema } from "./entity-id.schemas";
import { patientIdSchema } from "./patient.schemas";
import { visitIdSchema } from "./visit.schemas";

export const patientDocumentIdSchema: z.ZodType<PatientDocumentId, z.ZodTypeDef, string> = z
  .string()
  .min(1)
  .transform(asPatientDocumentId);

/**
 * 100 MB. Checked on the declared file size, before the bytes are read into
 * memory — tRPC has already buffered the multipart body by the time a
 * procedure runs, so the cheap refusal is the transport's (see services/api).
 *
 * The real cost of this number is concurrency: neither tRPC's multipart parse
 * nor Medplum's binary client can stream, so an in-flight upload is about one
 * file resident on the API server. Raising it much further wants a streaming
 * path rather than a bigger constant.
 */
export const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024;

/** FormData gives every field as a string or a File; zod checks which is which. */
const formFields = z.instanceof(FormData).transform((form) => Object.fromEntries(form.entries()));

/**
 * What every upload sends. `uploadedBy` is deliberately absent: the uploader is
 * whoever holds the token, so it comes from the request context and is not
 * something a caller gets to assert.
 *
 * `classification` is absent too, and deliberately. The purpose is chosen by
 * WHICH route you call, not by a string you send — a client that could name its
 * own purpose could file a capture as a record, and the vocabulary would be
 * open at the boundary instead of closed.
 */
const documentFields = {
  id: newEntityIdSchema,
  file: z
    .instanceof(File)
    .refine((file) => file.size > 0, "The document is empty")
    .refine((file) => file.size <= MAX_DOCUMENT_BYTES, `The document exceeds ${MAX_DOCUMENT_BYTES} bytes`),
  patientId: patientIdSchema,
  title: z.string().trim().min(1),
};

/** Material filed to be read. The visit is optional — it may have no encounter. */
export const uploadPatientRecordFormSchema = formFields.pipe(
  z.object({ ...documentFields, visitId: visitIdSchema.optional() }),
);

/** Content captured while observing. The visit is REQUIRED: a capture happens during care. */
export const captureObservationDocumentFormSchema = formFields.pipe(
  z.object({ ...documentFields, visitId: visitIdSchema }),
);

export type UploadPatientRecordInput = Omit<z.infer<typeof uploadPatientRecordFormSchema>, "file"> & {
  contentType: string;
  uploadedBy: ResourcePointer;
  content: Blob;
};

export type CaptureObservationDocumentInput = Omit<z.infer<typeof captureObservationDocumentFormSchema>, "file"> & {
  sourceFileName?: string;
  contentType: string;
  uploadedBy: ResourcePointer;
  content: Blob;
};

export const retractPatientDocumentInputSchema = z.object({ patientDocumentId: patientDocumentIdSchema });
export type RetractPatientDocumentInput = z.infer<typeof retractPatientDocumentInputSchema>;

/** Single PutObject limit; independent of the unchanged multipart limit. */
export const MAX_DIRECT_CAPTURE_BYTES = 5 * 1024 * 1024 * 1024;
export const prepareCaptureUploadInputSchema = z.object({
  patientId: patientIdSchema,
  visitId: visitIdSchema,
  contentType: z.string().trim().min(1).max(255),
});
export const releaseCaptureUploadInputSchema = prepareCaptureUploadInputSchema.extend({
  id: newEntityIdSchema,
  binaryId: z.string().regex(/^[A-Za-z0-9\-.]{1,64}$/),
});
export const fileUploadedCaptureInputSchema = releaseCaptureUploadInputSchema.extend({
  sourceFileName: z
    .string()
    .min(1)
    .regex(/^[^/\\\r\n\0]+$/, "Expected an original basename")
    .optional(),
  title: z.string().trim().min(1),
  declaredSize: z.number().int().positive().max(MAX_DIRECT_CAPTURE_BYTES),
});
export type PrepareCaptureUploadInput = z.infer<typeof prepareCaptureUploadInputSchema>;
export type ReleaseCaptureUploadInput = z.infer<typeof releaseCaptureUploadInputSchema>;
export type FileUploadedCaptureInput = z.infer<typeof fileUploadedCaptureInputSchema> & { uploadedBy: ResourcePointer };
